import React, { useState, useEffect } from 'react';
import { CHALLENGE_URL, COMMUNITY_PATH } from '../constants.ts';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/50' 
        : 'bg-white/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Enhanced Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <a href="/" className="group flex items-center gap-3">
                <img
                  src="/logo-151.svg"
                  alt=""
                  width={40}
                  height={40}
                  className="h-10 w-10 rounded-xl shadow-sm group-hover:scale-105 transition-transform duration-300"
                />
                <h1 className="text-2xl lg:text-3xl font-bold gradient-text group-hover:scale-105 transition-transform duration-300">
                  Jeu 151
                </h1>
              </a>
            </div>
          </div>

          {/* Enhanced Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <a 
              href="#features" 
              className="text-gray-700 hover:text-primary-600 transition-all duration-300 font-medium relative group"
            >
              Fonctionnalités
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a 
              href="#screenshots" 
              className="text-gray-700 hover:text-primary-600 transition-all duration-300 font-medium relative group"
            >
              Captures d'écran
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a 
              href={CHALLENGE_URL} 
              className="text-gray-700 hover:text-primary-600 transition-all duration-300 font-medium relative group"
            >
              Challenge IA
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a 
              href={COMMUNITY_PATH} 
              className="text-gray-700 hover:text-primary-600 transition-all duration-300 font-medium relative group"
            >
              Communauté
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a 
              href="#faq" 
              className="text-gray-700 hover:text-primary-600 transition-all duration-300 font-medium relative group"
            >
              FAQ
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full"></span>
            </a>
          </nav>

          {/* Enhanced Desktop CTA Button */}
          <div className="hidden md:block">
            <a
              href="https://game-i151.web.app"
              className="btn-primary btn-animate px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Tester l'application
            </a>
          </div>

          {/* Enhanced Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-primary-600 transition-all duration-300 p-2 rounded-lg hover:bg-gray-100 focus-ring"
              aria-label="Toggle mobile menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-4 pt-2 pb-4 space-y-2 bg-white/95 backdrop-blur-md border-t border-gray-200/50 shadow-lg">
              <a
                href="#features"
                onClick={closeMobileMenu}
                className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300"
              >
                Fonctionnalités
              </a>
              <a
                href="#screenshots"
                onClick={closeMobileMenu}
                className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300"
              >
                Captures d'écran
              </a>
              <a
                href={CHALLENGE_URL}
                onClick={closeMobileMenu}
                className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300"
              >
                Challenge IA
              </a>
              <a
                href={COMMUNITY_PATH}
                onClick={closeMobileMenu}
                className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300"
              >
                Communauté
              </a>
              <a
                href="#faq"
                onClick={closeMobileMenu}
                className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300"
              >
                FAQ
              </a>
              <div className="pt-4 pb-2 border-t border-gray-200/50">
                <a
                  href="https://game-i151.web.app"
                  onClick={closeMobileMenu}
                  className="block w-full text-center px-4 py-3 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Tester l'application
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 